import React from "react";
import TabSwitcher from "../../navigation/TabSwitcher";
import { Divider } from "@mui/material";

import { level1, slate } from "../../../theme/colors";
import { ACTIVE_LEVEL, selectedStateSx } from "../../../theme/activeStates";

/**
 * The tab strip under a sidebar header.
 *
 * `variant="underline"` is the flat strip the Loan Drilldown applicant and
 * property sidebars use: 13px labels in slate, no tint, and the selected tab in
 * the level-1 (teal) underline recipe — `selectedStateSx`, so the colors come
 * from the theme rather than this file. The default variant is the older,
 * compact tinted strip, kept for the sidebars that haven't moved.
 */
const SidebarTabSwitcher = ({
  tabs,
  value,
  onChange,
  hideDivider = false,
  variant = "default",
  sx,
  buttonSx,
}) => {
  const underline = variant === "underline";
  return (
    <>
      <TabSwitcher
        fontSize={underline ? "13px" : "11px"}
        tabs={tabs}
        wrap
        value={value}
        sx={{
          // The sidebar header is a flex column whose body is `height: 100%`;
          // without this the strip shrinks to its min-height and the Tabs
          // scroller clips the bottom of every tab — the underline with it.
          flexShrink: 0,
          ...(underline && { px: 1 }),
          ".MuiButtonBase-root": {
            minWidth: "unset",
            fontWeight: 400,
            py: 1.5,
            minHeight: "unset",
            textTransform: "none",
            ...(underline && {
              px: 1.75,
              py: 1.75,
              color: slate[600],
              letterSpacing: 0,
              backgroundColor: "#ffffff",
              borderBottom: "2px solid transparent",
              // Hover only darkens a RESTING tab — it must never repaint the
              // selected one (it used to turn the active tab black).
              "&:hover:not(.Mui-selected)": {
                color: slate[900],
                backgroundColor: "#ffffff",
              },
            }),
            ...buttonSx,
          },
          // Selected: white background, teal label, teal 2px bottom border
          // (the level-1 underline recipe). Doubled class + !important so it
          // beats TabSwitcher's tinted `.Mui-selected` and the theme's Tab
          // overrides regardless of stylesheet order.
          ...(underline && {
            "& .MuiTab-root.Mui-selected.Mui-selected": {
              color: `${
                selectedStateSx({
                  level: ACTIVE_LEVEL.PLACE,
                  variant: "underline",
                }).color
              } !important`,
              backgroundColor: "#ffffff !important",
              borderBottom: `2px solid ${level1.indicator} !important`,
              borderRadius: "0 !important",
              fontWeight: 500,
            },
          }),
          ...sx,
        }}
        onChange={onChange}
      />
      {hideDivider ? null : (
        <Divider sx={{ borderColor: underline ? slate[200] : "#eeeeee" }} />
      )}
    </>
  );
};

export default SidebarTabSwitcher;
